/**
 * EtInstrumenten — de meetinstrumenten uit de examenwerkbalk (Facet-blauwdruk):
 * meetlat (liniaal), geodriehoek, windroos en letterplankje. Eigen tekeningen
 * (SVG) in TestWisely-stijl; sleepbaar, meetlat/geodriehoek ook roteerbaar.
 *
 * Gedeelde sleep/rotatie-logica in useSleepbaar: pointercapture, pointercancel
 * en viewport-clamping (dezelfde lessen als bij de rekenmachine, review #3).
 */
import React, { useState, useRef, useCallback } from 'react';
import './EtInstrumenten.css';

function useSleepbaar(beginX, beginY) {
  const [pos, setPos] = useState({ x: beginX, y: beginY });
  const [hoek, setHoek] = useState(0);
  const ref = useRef(null);

  const startSleep = useCallback((e) => {
    e.preventDefault();
    const start = { px: e.clientX, py: e.clientY };
    const basis = { ...pos };
    const pid = e.pointerId;
    try { e.currentTarget.setPointerCapture(pid); } catch (_) {}
    const move = (ev) => {
      if (ev.pointerId !== pid) return;
      const x = Math.min(Math.max(basis.x + ev.clientX - start.px, -120), window.innerWidth - 60);
      const y = Math.min(Math.max(basis.y + ev.clientY - start.py, 4), window.innerHeight - 40);
      setPos({ x, y });
    };
    const stop = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', stop);
      window.removeEventListener('pointercancel', stop);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', stop);
    window.addEventListener('pointercancel', stop);
  }, [pos]);

  const startDraai = useCallback((e) => {
    e.preventDefault(); e.stopPropagation();
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const cx = rect.left + rect.width / 2, cy = rect.top + rect.height / 2;
    const beginHoek = Math.atan2(e.clientY - cy, e.clientX - cx) * 180 / Math.PI - hoek;
    const pid = e.pointerId;
    const move = (ev) => {
      if (ev.pointerId !== pid) return;
      setHoek(Math.atan2(ev.clientY - cy, ev.clientX - cx) * 180 / Math.PI - beginHoek);
    };
    const stop = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', stop);
      window.removeEventListener('pointercancel', stop);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', stop);
    window.addEventListener('pointercancel', stop);
  }, [hoek]);

  return { pos, hoek, ref, startSleep, startDraai };
}

/* ── Meetlat: 12 cm liniaal met mm-streepjes ── */
export function EtMeetlat({ open }) {
  // Zoals in Facet: twee rode grepen aan de UITEINDEN van de lijn, buiten de
  // schaal, zodat ze het aflezen niet in de weg zitten. De linkergreep is het
  // nulpunt en verplaatst de lat; de rechtergreep bepaalt in één gebaar de
  // lengte én de hoek. CM = 38 blijft: daar zijn de meetfiguren op afgestemd.
  const CM = 38, UIT = 42;                       // UIT = lijn steekt uit voorbij de schaal
  const [begin, setBegin] = useState({ x: 200, y: 160 });
  const [lengte, setLengte] = useState(14 * CM); // langer maken kan met de rechtergreep
  const [hoek, setHoek] = useState(0);
  if (!open) return null;

  const H = 52, Y = 12;                          // meetrand op y = Y
  const B = lengte + 2 * UIT;
  const aantalMm = Math.floor(lengte / CM * 10);
  const streepjes = [];
  for (let mm = 0; mm <= aantalMm; mm++) {
    const x = UIT + (mm / 10) * CM;
    const h = mm % 10 === 0 ? 15 : mm % 5 === 0 ? 10 : 6;
    streepjes.push(<line key={mm} x1={x} y1={Y} x2={x} y2={Y + h} />);
    if (mm % 10 === 0) streepjes.push(
      <text key={`t${mm}`} x={x} y={Y + 28} textAnchor="middle">{mm / 10}</text>);
  }

  const sleep = (soort) => (e) => {
    e.preventDefault(); e.stopPropagation();
    const pid = e.pointerId;
    try { e.currentTarget.setPointerCapture(pid); } catch (_) {}
    const start = { px: e.clientX, py: e.clientY, bx: begin.x, by: begin.y };
    const move = (ev) => {
      if (ev.pointerId !== pid) return;
      if (soort === 'verplaats') {
        setBegin({ x: start.bx + ev.clientX - start.px, y: start.by + ev.clientY - start.py });
      } else {
        const dx = ev.clientX - begin.x, dy = ev.clientY - begin.y;
        // De greep zit op het UITEINDE van de lijn, dus UIT pixels voorbij het
        // einde van de schaal. Die aftrekken, anders loopt de lat vooruit op
        // de muis.
        setLengte(Math.min(30 * CM, Math.max(4 * CM, Math.sqrt(dx * dx + dy * dy) - UIT)));
        setHoek(Math.atan2(dy, dx) * 180 / Math.PI);
      }
    };
    const stop = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', stop);
      window.removeEventListener('pointercancel', stop);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', stop);
    window.addEventListener('pointercancel', stop);
  };

  return (
    <div className="eti-zwevend eti-liniaal"
      role="img" aria-label={`Meetlat van ${(lengte / CM).toFixed(1)} centimeter`}
      style={{ left: begin.x - UIT, top: begin.y - Y,
               width: B, height: H,
               transformOrigin: `${UIT}px ${Y}px`, transform: `rotate(${hoek}deg)` }}>
      <svg width={B} height={H} viewBox={`0 0 ${B} ${H}`} className="eti-meetlat">
        {/* Overal op de lat pakken om hem te verplaatsen (melding Yasar). */}
        <path d={`M 0 ${Y} H ${B} L ${B - 6} ${H - 6} H 6 Z`} className="eti-lat-vlak"
          onPointerDown={sleep('verplaats')} />
        <line x1="0" y1={Y} x2={B} y2={Y} className="eti-lat-rand" />
        <g className="eti-streepjes">{streepjes}</g>
        <text x={UIT + 15} y={Y + 28} className="eti-cm">cm</text>
      </svg>
      {/* Precies op de uiteinden van de meetlijn, zoals in Facet. */}
      <button className="eti-greep links" onPointerDown={sleep('verplaats')}
        style={{ left: -9, top: Y - 9 }} aria-label="Verplaats de meetlat" />
      <button className="eti-greep rechts" onPointerDown={sleep('rek')}
        style={{ left: B - 9, top: Y - 9 }}
        aria-label="Sleep om de meetlat langer of korter te maken en te draaien" />
    </div>
  );
}

/* Sluiten doe je met dezelfde knop in de kopbalk (die schakelt aan/uit),
   zoals in Facet. Een kruisje óp het instrument zat in de weg bij het
   aflezen — melding Yasar 29-08. */

/* ── Geodriehoek: rechthoekige driehoek met gradenboog ── */
export function EtGeodriehoek({ open }) {
  // Echte geodriehoek: gelijkbenige rechthoekige driehoek. De gradenstreepjes
  // lopen vanuit het midden van de lange zijde naar buiten en eindigen precies
  // OP de schuine randen — daar lees je de hoek af. Met de rechter rode greep
  // maak je hem groter of kleiner en draai je hem, met de linker verplaats je.
  const CM = 38;
  const [begin, setBegin] = useState({ x: 150, y: 430 });
  const [halve, setHalve] = useState(5 * CM);   // halve basis = hoogte (45 graden)
  const [hoek, setHoek] = useState(0);
  if (!open) return null;

  const UIT = 26;                                // lijn steekt uit, grepen erbuiten
  const B = 2 * halve + 2 * UIT;
  const H = halve + 40;
  const BY = halve + 14;                         // de meetrand
  const MX = B / 2;                              // nulpunt: midden van de lange zijde

  // Waar snijdt de straal onder hoek g de schuine rand?
  const tot = (g) => {
    const r = g * Math.PI / 180;
    const noemer = g <= 90 ? Math.cos(r) + Math.sin(r) : Math.sin(r) - Math.cos(r);
    return halve / Math.max(0.001, noemer);
  };
  const punt = (g, terug) => {
    const r = g * Math.PI / 180, t = tot(g) - terug;
    return [MX + t * Math.cos(r), BY - t * Math.sin(r)];
  };

  const mm = [];
  const cmTot = Math.floor(halve / CM);
  for (let t = -cmTot * 10; t <= cmTot * 10; t++) {
    const x = MX + (t / 10) * CM;
    const h = t % 10 === 0 ? 13 : t % 5 === 0 ? 9 : 5;
    mm.push(<line key={`m${t}`} x1={x} y1={BY} x2={x} y2={BY - h} />);
    if (t % 10 === 0 && t !== 0) mm.push(
      <text key={`mt${t}`} x={x} y={BY + 15} textAnchor="middle">{Math.abs(t / 10)}</text>);
  }

  const graden = [];
  for (let g = 0; g <= 180; g++) {
    const lang = g % 10 === 0 ? 13 : g % 5 === 0 ? 8 : 4;
    const [x1, y1] = punt(g, 0);          // exact op de schuine rand
    const [x2, y2] = punt(g, lang);
    graden.push(<line key={`g${g}`} x1={x1} y1={y1} x2={x2} y2={y2}
      className={g % 10 === 0 ? 'eti-tien' : ''} />);
    if (g % 10 === 0 && g !== 0 && g !== 180) {
      const [tx, ty] = punt(g, lang + 11);
      graden.push(<text key={`gt${g}`} x={tx} y={ty} textAnchor="middle"
        dominantBaseline="middle">{g}</text>);
    }
  }

  const sleep = (soort) => (e) => {
    e.preventDefault(); e.stopPropagation();
    const pid = e.pointerId;
    try { e.currentTarget.setPointerCapture(pid); } catch (_) {}
    const start = { px: e.clientX, py: e.clientY, bx: begin.x, by: begin.y };
    const move = (ev) => {
      if (ev.pointerId !== pid) return;
      if (soort === 'verplaats') {
        setBegin({ x: start.bx + ev.clientX - start.px, y: start.by + ev.clientY - start.py });
      } else {
        const dx = ev.clientX - begin.x, dy = ev.clientY - begin.y;
        // De greep zit op het uiteinde van de lange zijde: dat is 2 x de halve
        // basis plus de uitloop, gerekend vanaf het nulpunt in het midden.
        const d = Math.sqrt(dx * dx + dy * dy) - UIT;
        setHalve(Math.min(16 * CM, Math.max(3 * CM, d / 2)));
        setHoek(Math.atan2(dy, dx) * 180 / Math.PI);
      }
    };
    const stop = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', stop);
      window.removeEventListener('pointercancel', stop);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', stop);
    window.addEventListener('pointercancel', stop);
  };

  return (
    <div className="eti-zwevend eti-geo-wrap" role="img"
      aria-label={`Geodriehoek, lange zijde ${(2 * halve / CM).toFixed(0)} centimeter`}
      style={{ left: begin.x - UIT, top: begin.y - BY, width: B, height: H,
               transformOrigin: `${UIT}px ${BY}px`, transform: `rotate(${hoek}deg)` }}>
      <svg width={B} height={H} viewBox={`0 0 ${B} ${H}`} className="eti-geo">
        {/* Overal op de driehoek pakken om hem te verplaatsen. */}
        <polygon points={`${UIT},${BY} ${B - UIT},${BY} ${MX},${BY - halve}`}
          className="eti-geo-vlak" onPointerDown={sleep('verplaats')} />
        <line x1={UIT - UIT} y1={BY} x2={B} y2={BY} className="eti-meetrand" />
        <g className="eti-graden">{graden}</g>
        <g className="eti-streepjes">{mm}</g>
        <line x1={MX} y1={BY} x2={MX} y2={BY - halve} className="eti-middellijn" />
        <text x={MX} y={BY + 15} textAnchor="middle" className="eti-nul">0</text>
      </svg>
      <button className="eti-greep links" onPointerDown={sleep('verplaats')}
        style={{ left: -9, top: BY - 9 }} aria-label="Verplaats de geodriehoek" />
      <button className="eti-greep rechts" onPointerDown={sleep('rek')}
        style={{ left: B - 9, top: BY - 9 }}
        aria-label="Sleep om de geodriehoek groter of kleiner te maken en te draaien" />
    </div>
  );
}

/* ── Windroos: kompasroos met hoofd- en tussenrichtingen ── */
export function EtWindroos({ open }) {
  // Zoals in Facet: de lichte spil is het middelpunt (verslepen = verplaatsen),
  // de rode greep zit op de NULSTREEP. Die greep doet twee dingen tegelijk:
  // de richting waarin je hem trekt draait de hele schaal, en de afstand tot
  // het middelpunt bepaalt hoe groot de gradenboog is.
  const [middel, setMiddel] = useState({ x: 620, y: 330 });
  const [straal, setStraal] = useState(150);
  const [rotatie, setRotatie] = useState(0);        // richting van de nulstreep
  if (!open) return null;

  const MARGE = 30;
  const MAAT = 2 * (straal + MARGE);
  const C = straal + MARGE;                          // middelpunt in de svg
  const punt = (graden, r) => {
    const rad = (rotatie + graden - 90) * Math.PI / 180;
    return [C + r * Math.cos(rad), C + r * Math.sin(rad)];
  };

  const streepjes = [];
  for (let g = 0; g < 360; g++) {
    const lang = g % 10 === 0 ? Math.max(10, straal * 0.09) : g % 5 === 0 ? 7 : 4;
    const [x1, y1] = punt(g, straal);
    const [x2, y2] = punt(g, straal - lang);
    streepjes.push(<line key={g} x1={x1} y1={y1} x2={x2} y2={y2}
      className={g % 10 === 0 ? 'eti-tien' : ''} />);
    if (g % 10 === 0) {
      const [tx, ty] = punt(g, straal - lang - 12);
      streepjes.push(
        <text key={`t${g}`} x={tx} y={ty} textAnchor="middle" dominantBaseline="middle"
          transform={`rotate(${rotatie + g} ${tx} ${ty})`}>{g}</text>);
    }
  }

  const [rx, ry] = punt(0, straal);

  const sleepSpil = (e) => {
    e.preventDefault(); e.stopPropagation();
    const pid = e.pointerId;
    const start = { px: e.clientX, py: e.clientY, mx: middel.x, my: middel.y };
    try { e.currentTarget.setPointerCapture(pid); } catch (_) {}
    const move = (ev) => {
      if (ev.pointerId !== pid) return;
      setMiddel({ x: start.mx + ev.clientX - start.px, y: start.my + ev.clientY - start.py });
    };
    const stop = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', stop);
      window.removeEventListener('pointercancel', stop);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', stop);
    window.addEventListener('pointercancel', stop);
  };

  // De rode greep: richting = rotatie, afstand = straal. Eén gebaar, twee effecten.
  const sleepGreep = (e) => {
    e.preventDefault(); e.stopPropagation();
    const pid = e.pointerId;
    try { e.currentTarget.setPointerCapture(pid); } catch (_) {}
    const move = (ev) => {
      if (ev.pointerId !== pid) return;
      const dx = ev.clientX - middel.x, dy = ev.clientY - middel.y;
      const afstand = Math.sqrt(dx * dx + dy * dy);
      setStraal(Math.min(360, Math.max(70, afstand)));
      setRotatie(Math.round((Math.atan2(dy, dx) * 180 / Math.PI + 90 + 360) % 360));
    };
    const stop = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', stop);
      window.removeEventListener('pointercancel', stop);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', stop);
    window.addEventListener('pointercancel', stop);
  };

  return (
    <div className="eti-zwevend eti-gradenboog" role="img"
      aria-label={`Windroos, nulstreep op ${rotatie} graden, straal ${Math.round(straal)} pixels`}
      style={{ left: middel.x - C, top: middel.y - C, width: MAAT, height: MAAT }}>
      <svg width={MAAT} height={MAAT} viewBox={`0 0 ${MAAT} ${MAAT}`} className="eti-windroos">
        <circle cx={C} cy={C} r={straal} className="eti-ring" />
        <g className="eti-graden">{streepjes}</g>
        <line x1={C} y1={C} x2={rx} y2={ry} className="eti-arm" />
      </svg>
      <button className="eti-spilknop" onPointerDown={sleepSpil}
        style={{ left: C - 9, top: C - 9 }} aria-label="Verplaats de windroos" />
      <button className="eti-greep nul" onPointerDown={sleepGreep}
        style={{ left: rx - 9, top: ry - 9 }}
        aria-label={`Nulstreep: sleep om te draaien en groter te maken, nu ${rotatie} graden`} />
    </div>
  );
}

/* ── Letterplankje: speciale tekens invoegen in het antwoord ── */
const TEKENS = ['é', 'è', 'ë', 'ê', 'á', 'à', 'ä', 'ï', 'í', 'ó', 'ö', 'ô', 'ú', 'ü', 'ç', 'ñ', '°', '½', '¾', '×', '÷', '≈', '≠', '±'];
export function EtLetterplankje({ open, onTeken }) {
  const s = useSleepbaar(860, 340);
  if (!open) return null;
  return (
    <div ref={s.ref} className="eti-zwevend eti-plankje" role="dialog" aria-label="Letterplankje"
      style={{ left: s.pos.x, top: s.pos.y }}>
      <div className="eti-plankje-kop" onPointerDown={s.startSleep}>
        {/* Geen kruisje: × staat ook in TEKENS als vermenigvuldigingsteken.
            Sluiten gaat met de knop in de werkbalk, zoals bij de andere
            instrumenten. */}
        <span>Letterplankje</span>
      </div>
      <div className="eti-plankje-rooster">
        {TEKENS.map((t) => (
          <button key={t} onClick={() => onTeken && onTeken(t)}
            aria-label={`Voeg ${t} in`}>{t}</button>
        ))}
      </div>
    </div>
  );
}
